import type {limitNameMap} from "./constants";
import type {FetchState} from "@atg-shared/fetch-types";

export type Maybe<T> = T | null | undefined;

export type Prettify<T> = {
    [K in keyof T]: T[K];
};

export type RgsLimitPeriod = "day" | "week" | "month";

type ValidationAmountType = "MAX_DAY" | "MAX_WEEK" | "MAX_MONTH" | "MIN_VALUE";

export type ValidationAmount = {
    type: ValidationAmountType;
    amount: number;
};

export type PeriodicLimit = {
    limit: number;
    remaining: number;
    futureLimit?: number;
    futureActivationTime?: string;
};

export type DepositLimits = {
    [period in LimitPeriod]?: PeriodicLimit;
};

export type GetDepositLimitsResponse = {
    depositLimits: DepositLimits;
    maxLimits?: Array<MaxLimit>;
    validationAmounts: Array<ValidationAmount>;
};
export type DepositLimitsState = {
    maxLimits?: Array<MaxLimit>;
} & DepositLimits;

export type PutDepositLimitsResponse = {
    depositLimits: {
        [period in LimitPeriod]: PeriodicLimit;
    };
    maxLimits?: Array<MaxLimit>;
};

export type MaxLimitType =
    | "CUSTOMER_SERVICE"
    | "BASE_LIMIT_YOUNGEST_ADULT"
    | "BASE_LIMIT_YOUNG_ADULT"
    | "BASE_LIMIT_ADULT";

export type MaxLimit = {
    maxLimitAmount: number;
    maxLimitType: MaxLimitType;
};

export type DepositLimitsResponse = {
    maxLimits: Array<MaxLimit> | [];
    depositLimits: DepositLimits;
    validationAmounts: Array<ValidationAmount> | [];
};

// A user is not allowed to have partial deposit limits set (See SPECTRA-71 for more information). Type should probably be updated going forward.
export type MultiPeriodicLimit = {
    businessOperationId?: string;
    depositLimits?: DepositLimits;
    maxLimits?: Array<MaxLimit>;
    validationAmounts?: Array<ValidationAmount>;
};

export type RgsPeriodicLimit = {
    limit: number | null;
    remaining: number | null;
    validFrom: string;
    validTo: string | null;
    upcoming: RgsPeriodicLimit | null;
    createdBy: string;
    maxLimit?: number;
};

export enum VerticalEnum {
    CASINO = "CASINO",
    HORSE_BETTING = "HORSE_BETTING",
    SPORTSBOOK = "SPORTSBOOK",
}

export type NetlossVertical = "casino" | "horse" | "sport";

export enum RgsLimitTypeEnum {
    LOSS = "netLossLimits",
    TIME = "playTimeLimits",
    LOGIN = "loginTimeLimits",
}

export type PartialRgsPeriodicLimit = Partial<Record<RgsLimitPeriod, RgsPeriodicLimit>>;

export type NetLossLimitsResponse = {
    netLossLimits: Record<VerticalEnum, Record<"week", LimitObject>>;
};
export type NetLossLimits = Record<VerticalEnum, Record<"week", LimitObject>>;
export type NetLossLimitsPayload = {
    CASINO?: number | null;
    HORSE_BETTING?: number | null;
    SPORTSBOOK?: number | null;
};

export type RgsVerticalLimit = Prettify<
    PartialRgsPeriodicLimit & {
        set?: boolean;
    }
>;

export type SimplifiedRgsLimits = Record<NetlossVertical, number>;

export type NullableSimplifiedRgsLimits = {
    [K in keyof SimplifiedRgsLimits]: SimplifiedRgsLimits[K] | null;
};

export type RgsLimit = {
    CASINO?: FetchState<RgsVerticalLimit>;
    HORSE_BETTING?: FetchState<RgsVerticalLimit>;
    SPORTSBOOK?: FetchState<RgsVerticalLimit>;
};

export type NetLossLimitResponse = {
    netLossLimits: {
        HORSE_BETTING: PartialRgsPeriodicLimit | Record<string, never>;
        CASINO: PartialRgsPeriodicLimit | Record<string, never>;
        SPORTSBOOK: PartialRgsPeriodicLimit | Record<string, never>;
    };
};

export type PlayTimeLimitResponse = {
    playTimeLimits: {
        CASINO: {
            day: RgsPeriodicLimit;
        };
    };
};

export type RgsLimits = {
    netLossLimits: RgsLimit;
    playTimeLimits: RgsLimit;
    loginTimeLimits: FetchState<RgsVerticalLimit>;
};

export type LimitPeriod = "weeklyLimit" | "dailyLimit" | "monthlyLimit";

export type CalendarPeriod = "day" | "week" | "month";

export type DepositLimitsPayload = Partial<{
    dailyLimit: number | null;
    weeklyLimit: number | null;
    monthlyLimit: number | null;
}>;

export type LimitObject = {
    limit?: number | null;
    remaining?: number | null;
    validTo?: string | null;
    validFrom?: string | null;
    maxLimit?: number | null;
    createdBy?: string | null;
    upcoming?: Exclude<LimitObject, "upcoming"> | null;
};

export type LoginTimeLimits = {
    day: LimitObject;
    week: LimitObject;
    month: LimitObject;
};
export type LoginTimeLimitsResponse = {
    loginTimeLimits: LoginTimeLimits;
};
export type LoginTimeLimitsPayload = Partial<{
    dailyLimit: number | null;
    weeklyLimit: number | null;
    monthlyLimit: number | null;
}>;

export type CasinoTimeLimits = LimitObject;
export type CasinoTimeLimitsResponse = {
    playTimeLimits: {
        CASINO: {
            day: CasinoTimeLimits;
        };
    };
};
export type CasinoTimeLimitsPayload = Partial<{
    CASINO: number | null;
}>;

export type LimitVariant = "deposit" | "login" | "netloss" | "playtime";

export type LimitNameMapKey = keyof ReturnType<typeof limitNameMap>;

// types for deposit limits setup modal
export type ModalStep = "monthlyLimit" | "weeklyLimit" | "dailyLimit";
export type ModalStatus = "idle" | "confirm" | "success" | "dismiss" | "error";

export type BudgetMessage = {
    id: string;
    text: string;
    type: "error" | "success";
};

// component props to be used in lazy loaded components
export type DepositLimitsSetupDismissProps = {
    onDismiss: () => void;
    onContinue: () => void;
};
export type LimitChangesSuccessParams = {
    increased: LimitNameMapKey[];
    decreased: LimitNameMapKey[];
    // for net loss limits & time limits
    removed?: LimitNameMapKey[];
};
export type LimitModalProps = {
    open: boolean;
    onClose: () => void;
    onSuccess?: (limitChanges: LimitChangesSuccessParams) => void;
    modalSource?: "registration" | "my-limits";
};
